import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Form, Button, Card, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaTimesCircle } from 'react-icons/fa';

const Gallery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData || '{}');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const galleryData = location.state?.galleryData || null;
  const isEdit = !!galleryData;

  const [form, setForm] = useState({
    gallery_name: '',
    event_date: new Date().toISOString().split('T')[0],
    action: 'CREATE',
    school_id: userObj?.school_id,
    createdby: userObj?.user_id,
    lastmodifiedby: userObj?.user_id,
    gallery_id: null,
  });

  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]); // normalized
  const [uploadProgress, setUploadProgress] = useState(null); // { loaded, total, percent }
  const [previewItem, setPreviewItem] = useState(null); // { src, label, isVideo }
  const fileInputRef = useRef();

  // ---------- Helpers ----------
  const isAbsoluteUrl = (s) => /^https?:\/\//i.test(s || '');

  const safeFileNameFromUrl = (href) => {
    try {
      const u = new URL(href);
      const pathLast = (u.pathname.split('/').pop() || '').trim();
      return pathLast || 'file';
    } catch {
      return (href || '').split('/').pop() || 'file';
    }
  };

  const normalizeFileItem = (f) => {
    if (!f) return null;

    if (typeof f === 'string') {
      if (isAbsoluteUrl(f)) {
        const label = decodeURIComponent(safeFileNameFromUrl(f));
        return { url: f, api_url: null, blob: null, label };
      }
      const label = decodeURIComponent((f.split('/').pop() || '').trim());
      return {
        url: null,
        api_url: `${baseUrl}/main/gallery/get-image/${encodeURIComponent(f)}`,
        blob: f,
        label,
      };
    }

    const blob = f.blob || null;
    const url = f.url || null;
    const api_url =
      f.api_url ||
      (blob ? `${baseUrl}/main/gallery/get-image/${encodeURIComponent(blob)}` : null);

    const label =
      f.label ||
      (blob ? decodeURIComponent((blob.split('/').pop() || '').trim()) : decodeURIComponent(safeFileNameFromUrl(url)));

    return { url, api_url, blob, label };
  };

  const buildFileHref = (item) => item?.url || item?.api_url || '#';

  const extractFileNameForDelete = (item) => {
    if (!item) return '';
    if (item.blob) return decodeURIComponent((item.blob.split('/').pop() || '').trim());
    if (item.url) return decodeURIComponent(safeFileNameFromUrl(item.url));
    if (typeof item === 'string') return decodeURIComponent((item.split('/').pop() || '').trim());
    return '';
  };

  // File type helpers
  const getExt = (name = '') => (name.split('.').pop() || '').toLowerCase();
  const isImageExt = (ext) =>
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  const isVideoExt = (ext) => ['mp4', 'webm', 'ogg', 'mov', 'm4v'].includes(ext);
  const isPdfExt = (ext) => ext === 'pdf';

  // Format bytes helper
  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Inline progress overlay rendered on top of any thumbnail
  const renderProgressOverlay = () => {
    if (uploadProgress === null) return null;
    const { percent, loaded, total } = uploadProgress;
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 8,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          zIndex: 10,
        }}
      >
        <svg width="48" height="48" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="19" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="4" />
          <circle
            cx="24" cy="24" r="19"
            fill="none"
            stroke="#25D366"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 19}`}
            strokeDashoffset={`${2 * Math.PI * 19 * (1 - percent / 100)}`}
            transform="rotate(-90 24 24)"
            style={{ transition: 'stroke-dashoffset 0.25s ease' }}
          />
          <text x="24" y="28" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">
            {percent}%
          </text>
        </svg>
        <div style={{ fontSize: 10, color: '#fff', fontWeight: 600 }}>
          {formatBytes(loaded)}/{formatBytes(total)}
        </div>
      </div>
    );
  };

  // ---------- Effects ----------
  useEffect(() => {
    if (isEdit) {
      const formattedDate =
        galleryData.event_date?.split('T')[0] ||
        galleryData.event_date?.split(' ')[0] ||
        new Date().toISOString().split('T')[0];

      setForm((prev) => ({
        ...prev,
        ...galleryData,
        event_date: formattedDate,
        action: 'UPDATE',
        createdby: galleryData.createdby || userObj?.user_id,
        lastmodifiedby: userObj?.user_id,
      }));

      axios
        .get(`${baseUrl}/main/gallery/files/${encodeURIComponent(galleryData.gallery_name)}`, {
          params: { school_id: userObj?.school_id },
        })
        .then((res) => {
          const raw = res.data?.files || [];
          const normalized = raw.map(normalizeFileItem).filter(Boolean);
          setExistingFiles(normalized);
        })
        .catch((err) => {
          console.error(err);
          toast.error('Failed to load gallery files');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // ---------- New file previews ----------
  const newFilePreviews = useMemo(() => {
    return files.map((file) => ({ file, url: URL.createObjectURL(file) }));
  }, [files]);

  useEffect(() => {
    return () => {
      newFilePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [newFilePreviews]);

  // ---------- Handlers ----------
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
  };

  const handleFileRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExistingFileDelete = async (fileItem) => {
    const fileName = extractFileNameForDelete(fileItem);
    const galleryName = form.gallery_name;

    if (!fileName) {
      toast.error('Could not determine file name');
      return;
    }
    if (!window.confirm(`Delete file "${fileName}"?`)) return;

    try {
      await axios.delete(
        `${baseUrl}/main/gallery/files/${encodeURIComponent(galleryName)}/${encodeURIComponent(
          fileName
        )}`,
        { params: { school_id: form.school_id } }
      );

      toast.success('File deleted');
      setExistingFiles((prev) =>
        prev.filter((it) => {
          if (fileItem.url) return it.url !== fileItem.url;
          if (fileItem.blob) return it.blob !== fileItem.blob;
          return it.label !== fileItem.label;
        })
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete file');
    }
  };

  const refreshExistingFiles = async (galleryName, schoolId) => {
    try {
      const res = await axios.get(
        `${baseUrl}/main/gallery/files/${encodeURIComponent(galleryName)}`,
        { params: { school_id: schoolId } }
      );
      const raw = res.data?.files || [];
      const normalized = raw.map(normalizeFileItem).filter(Boolean);
      setExistingFiles(normalized);
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${baseUrl}/main/gallery`, form);

      if (files.length > 0) {
        const fd = new FormData();
        fd.append('gallery_name', form.gallery_name);
        fd.append('school_id', form.school_id);
        files.forEach((file) => fd.append('files', file));

        // Calculate total size for display
        const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
        setUploadProgress({ loaded: 0, total: totalBytes, percent: 0 });

        await axios.post(`${baseUrl}/main/gallery/upload/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const loaded = progressEvent.loaded;
            const total = progressEvent.total || totalBytes;
            const percent = Math.min(Math.round((loaded / total) * 100), 100);
            setUploadProgress({ loaded, total, percent });
          },
        });

        setUploadProgress(null);
      }

      toast.success(isEdit ? 'Gallery updated successfully' : 'Gallery created successfully');

      if (!isEdit) {
        setForm({
          gallery_name: '',
          event_date: new Date().toISOString().split('T')[0],
          action: 'CREATE',
          school_id: userObj?.school_id,
          createdby: userObj?.user_id,
          lastmodifiedby: userObj?.user_id,
          gallery_id: null,
        });
        setFiles([]);
        setExistingFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        await refreshExistingFiles(form.gallery_name, form.school_id);
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error(err);
      setUploadProgress(null);
      toast.error('Failed to save gallery');
    }
  };

  const handleReset = () => {
    setForm({
      gallery_name: '',
      event_date: new Date().toISOString().split('T')[0],
      action: 'CREATE',
      school_id: userObj?.school_id,
      createdby: userObj?.user_id,
      lastmodifiedby: userObj?.user_id,
      gallery_id: null,
    });
    setFiles([]);
    setExistingFiles([]);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---------- Preview renderers ----------
  const renderExistingPreview = (item, idx) => {
    const src = buildFileHref(item);
    const ext = getExt(item?.label);

    if (isImageExt(ext)) {
      return (
        <div key={`ex-${idx}`} className="me-3 mb-3" style={{ width: 170 }}>
          <div style={{ position: 'relative' }}>
            <img
              src={src}
              alt={item.label}
              style={{
                width: 170,
                height: 120,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => setPreviewItem({ src, label: item.label, isVideo: false })}
            />
            <FaTimesCircle
              className="text-danger"
              onClick={() => handleExistingFileDelete(item)}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: 6,
                right: 6,
                background: '#fff',
                borderRadius: '50%',
              }}
              title="Delete"
            />
          </div>
          <div
            title={item.label}
            style={{
              fontSize: 12,
              marginTop: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.label}
          </div>
        </div>
      );
    }

    if (isVideoExt(ext)) {
      return (
        <div key={`ex-${idx}`} className="me-3 mb-3" style={{ width: 260 }}>
          <div style={{ position: 'relative' }}>
            <video
              src={src}
              controls
              style={{
                width: 260,
                height: 150,
                borderRadius: 8,
                border: '1px solid #ddd',
                background: '#000',
                cursor: 'pointer',
              }}
              onClick={() => setPreviewItem({ src, label: item.label, isVideo: true })}
            />
            <FaTimesCircle
              className="text-danger"
              onClick={() => handleExistingFileDelete(item)}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: 6,
                right: 6,
                background: '#fff',
                borderRadius: '50%',
              }}
              title="Delete"
            />
          </div>
          <div
            title={item.label}
            style={{
              fontSize: 12,
              marginTop: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.label}
          </div>
        </div>
      );
    }

    if (isPdfExt(ext)) {
      return (
        <div key={`ex-${idx}`} className="mb-3" style={{ width: '100%' }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
            <FaTimesCircle
              className="text-danger"
              onClick={() => handleExistingFileDelete(item)}
              style={{ cursor: 'pointer' }}
              title="Delete"
            />
          </div>
          <iframe
            src={src}
            title={item.label}
            style={{ width: '100%', height: 380, border: '1px solid #ddd', borderRadius: 8 }}
          />
        </div>
      );
    }

    return (
      <div key={`ex-${idx}`} className="me-3 mb-2 d-flex align-items-center">
        <span style={{ fontSize: 13 }}>{item.label}</span>
        <a className="ms-2" href={src} target="_blank" rel="noopener noreferrer">
          Open
        </a>
        <FaTimesCircle
          className="text-danger ms-2"
          onClick={() => handleExistingFileDelete(item)}
          style={{ cursor: 'pointer' }}
          title="Delete"
        />
      </div>
    );
  };

  const renderNewPreview = (p, idx) => {
    const ext = getExt(p.file?.name);

    if (isImageExt(ext)) {
      return (
        <div key={`nw-${idx}`} className="me-3 mb-3" style={{ width: 170 }}>
          <div style={{ position: 'relative' }}>
            <img
              src={p.url}
              alt={p.file.name}
              style={{
                width: 170,
                height: 120,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => setPreviewItem({ src: p.url, label: p.file.name, isVideo: false })}
            />
            {renderProgressOverlay()}
            <FaTimesCircle
              className="text-danger"
              onClick={() => handleFileRemove(idx)}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: 6,
                right: 6,
                background: '#fff',
                borderRadius: '50%',
                zIndex: 11,
              }}
              title="Remove"
            />
          </div>
          <div
            title={p.file.name}
            style={{
              fontSize: 12,
              marginTop: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {p.file.name}
          </div>
        </div>
      );
    }

    if (isVideoExt(ext)) {
      return (
        <div key={`nw-${idx}`} className="me-3 mb-3" style={{ width: 260 }}>
          <div style={{ position: 'relative' }}>
            <video
              src={p.url}
              controls
              style={{
                width: 260,
                height: 150,
                borderRadius: 8,
                border: '1px solid #ddd',
                background: '#000',
                cursor: 'pointer',
              }}
              onClick={() => setPreviewItem({ src: p.url, label: p.file.name, isVideo: true })}
            />
            {renderProgressOverlay()}
            <FaTimesCircle
              className="text-danger"
              onClick={() => handleFileRemove(idx)}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: 6,
                right: 6,
                background: '#fff',
                borderRadius: '50%',
                zIndex: 11,
              }}
              title="Remove"
            />
          </div>
          <div
            title={p.file.name}
            style={{
              fontSize: 12,
              marginTop: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {p.file.name}
          </div>
        </div>
      );
    }

    if (isPdfExt(ext)) {
      return (
        <div key={`nw-${idx}`} className="mb-3" style={{ width: '100%' }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div style={{ fontSize: 13, fontWeight: 600 }}>{p.file.name}</div>
            <FaTimesCircle
              className="text-danger"
              onClick={() => handleFileRemove(idx)}
              style={{ cursor: 'pointer' }}
              title="Remove"
            />
          </div>
          <iframe
            src={p.url}
            title={p.file.name}
            style={{ width: '100%', height: 380, border: '1px solid #ddd', borderRadius: 8 }}
          />
        </div>
      );
    }

    return (
      <div key={`nw-${idx}`} className="me-3 mb-2 d-flex align-items-center">
        <span style={{ fontSize: 13 }}>{p.file.name}</span>
        <FaTimesCircle
          className="text-danger ms-2"
          onClick={() => handleFileRemove(idx)}
          style={{ cursor: 'pointer' }}
          title="Remove"
        />
      </div>
    );
  };

  return (
    <div className="pageBody">
      <ToastContainer />

      <Modal show={!!previewItem} onHide={() => setPreviewItem(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewItem?.label}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center', background: '#f8f8f8' }}>
          {previewItem?.isVideo ? (
            <video src={previewItem?.src} controls style={{ maxWidth: '100%', maxHeight: '70vh' }} autoPlay />
          ) : (
            <img src={previewItem?.src} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          )}
        </Modal.Body>
      </Modal>

      <div className="height100 innerPage">
        <Card>
          {/* ✅ Remove scroll from Card.Body */}
          <Card.Body style={{ paddingRight: '10px' }}>
            <Form onSubmit={handleSubmit}>
              <div className="innerPageBody">
                <Row>
                  <Col xs={12}>
                    <h6 className="commonSectionTitle">{isEdit ? 'Edit Gallery' : 'Add Gallery'}</h6>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Gallery Name <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          required
                          type="text"
                          id="gallery_name"
                          value={form.gallery_name}
                          onChange={handleInputChange}
                          placeholder="Enter Gallery Name"
                          maxLength={100}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Event Date <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          required
                          type="date"
                          id="event_date"
                          value={form.event_date}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={12} lg={12} xxl={12}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Upload Images/Videos</Form.Label>
                        <Form.Control type="file" multiple ref={fileInputRef} onChange={handleFileChange} />

                        {/* ✅ ONLY FILES AREA SCROLLS */}
                        <div
                          className="mt-3"
                          style={{
                            maxHeight: '320px',   // change as you like
                            overflowY: 'auto',
                            border: '1px solid #e5e5e5',
                            borderRadius: 8,
                            padding: 10,
                            background: '#fafafa',
                          }}
                        >
                          {newFilePreviews.length > 0 && (
                            <>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                                Selected Files (Preview)
                              </div>
                              <div className="d-flex flex-wrap">
                                {newFilePreviews.map((p, idx) => renderNewPreview(p, idx))}
                              </div>
                            </>
                          )}

                          {existingFiles.length > 0 && (
                            <>
                              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                                Existing Files
                              </div>
                              <div className="d-flex flex-wrap">
                                {existingFiles.map((item, idx) => renderExistingPreview(item, idx))}
                              </div>
                            </>
                          )}

                          {newFilePreviews.length === 0 && existingFiles.length === 0 && (
                            <div style={{ fontSize: 13, color: '#777' }}>
                              No files selected / uploaded.
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    className="btn btn-info clearBtn"
                    onClick={handleReset}
                    disabled={uploadProgress !== null}
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="danger"
                    className="secondaryBtn"
                    onClick={() => navigate(-1)}
                    disabled={uploadProgress !== null}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-success primaryBtn"
                    disabled={uploadProgress !== null}
                  >
                    {isEdit ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Gallery;